import { fetchAuthSession } from '@aws-amplify/auth';
import { WHISPERBI_SECURITY_API } from '../env';
import env from '../config/env';

const USER_ACCOUNT_BASE_URL = WHISPERBI_SECURITY_API;
const RESOURCE_URL = USER_ACCOUNT_BASE_URL;

const defaultHeaders = env.API_KEY
  ? { 'x-api-key': env.API_KEY }
  : {};

async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    //const accessToken = tokens?.accessToken?.toString();

    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
  /*
    if (accessToken) {
      return { Authorization: `Bearer ${accessToken}` };
    }
  */
  } catch (error) {
    console.error('UserProfileService.getAuthHeader: unable to fetch session', error);
  }

  return {};
}

async function getCurrentUserId() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idTokenSub = tokens?.idToken?.payload?.sub;
    const accessTokenSub = tokens?.accessToken?.payload?.sub;

    return idTokenSub || accessTokenSub || null;
  } catch (error) {
    console.error('UserProfileService.getCurrentUserId: unable to fetch session', error);
    return null;
  }
}

/*
* A wrapper around fetch to include authentication headers and handle responses
* - input: URL or Request object
* - init: fetch options (method, headers, body, etc.)
*/
async function fetchWithAuth(input, init = {}) {
  const authHeader = await getAuthHeader();

  const baseHeaders = init.body
    ? { 'Content-Type': 'application/json', ...(init.headers || {}) }
    : { ...(init.headers || {}) };

  const headers = { ...defaultHeaders, ...baseHeaders, ...authHeader };
  const allHeaders = {
    ...init,
    headers,
    mode: 'cors',
    cache: 'no-cache'
  };

  const response = await fetch(input, allHeaders);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${errorBody || 'Unexpected error'}`);
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text || null;
  }
}


function parseDynamoValue(attributeValue) {
  if (!attributeValue || typeof attributeValue !== 'object') {
    return attributeValue;
  }

  if ('S' in attributeValue) {
    return attributeValue.S;
  }

  if ('N' in attributeValue) {
    const parsed = Number(attributeValue.N);
    return Number.isNaN(parsed) ? attributeValue.N : parsed;
  }

  if ('BOOL' in attributeValue) {
    return Boolean(attributeValue.BOOL);
  }

  if ('NULL' in attributeValue) {
    return null;
  }

  if ('M' in attributeValue) {
    return transformDynamoItem(attributeValue.M);
  }

  if ('L' in attributeValue && Array.isArray(attributeValue.L)) {
    return attributeValue.L.map(parseDynamoValue);
  }

  if ('SS' in attributeValue && Array.isArray(attributeValue.SS)) {
    return [...attributeValue.SS];
  }

  if ('NS' in attributeValue && Array.isArray(attributeValue.NS)) {
    return attributeValue.NS.map((value) => {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    });
  }

  if ('BS' in attributeValue && Array.isArray(attributeValue.BS)) {
    return [...attributeValue.BS];
  }

  return attributeValue;
}

function transformDynamoItem(item) {
  if (!item || typeof item !== 'object') {
    return item;
  }

  return Object.entries(item).reduce((accumulator, [key, value]) => {
    accumulator[key] = parseDynamoValue(value);
    return accumulator;
  }, {});
}

function extractCommonProfileFields(profile) {
  const companyName = profile?.company?.name || profile?.company?.legal_name || '';
  const employeesCount = profile?.company?.employees_count;
  const loadDemoData = profile?.load_demo_data ?? profile?.loadDemoData;
  const userId = profile?.user_id || profile?.userId || null;
  const accountId = profile?.account_id || profile?.accountId || null;

  return {
    companyName,
    company_name: companyName,
    employeesCount,
    employees_count: employeesCount,
    loadDemoData,
    load_demo_data: loadDemoData,
    userId,
    user_id: userId,
    accountUuid: accountId,
    account_uuid: accountId
  };
}

function normalizeProfileFromDynamo(rawProfile) {
  const profile = transformDynamoItem(rawProfile);

  return {
    ...profile,
    ...extractCommonProfileFields(profile)
  };
}

function normalizePlainProfile(profile) {
  return {
    ...profile,
    ...extractCommonProfileFields(profile)
  };
}

function generateAccountUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const timestampSegment = Date.now().toString(16);
  const randomSegment = Math.random().toString(16).slice(2, 10);

  return `${timestampSegment}-${randomSegment}`;
}

const UserProfileService = {


  /**
   * Retrieve the current user's profile from the backend
   * @returns {Promise<Object|null>} The user profile object or null if not found
   * @throws {Error} If unable to get the user ID or if the fetch fails
   * Example json returned:
   * 
   {
        "account_id": "5e2a5b3f-0a79-4a66-bd2f-0c1d7a4a6a12",
        "tenant_id": "9b6a2d83-5d1e-41c6-8b6d-3c7a4c9d2f55",
        "plan": "free",
        "user_id": "27638d76-598f-4216-8e81-9e7bf2cdf010",
        "company": {
          "company_id": "7f1e2a94-3c0b-4c8a-9c02-6d9f8c4b2a11",
          "name": "Izzi Telecom",
          "slug": "izzi-telecom",
          "legal_name": "",
          "taxid": "",
          "physical_address": {
            "street": "",
            "neighborhood": "",
            "city": "",
            "postal_code": "",
            "country": ""
          },
          "fiscal_address": {
            "street": "",
            "neighborhood": "",
            "city": "",
            "postal_code": "",
            "country": ""
          },
          "contact_phone": "",
          "contact_email": "",
          "facebook": "",
          "linkedin": "",
          "employees_count": 2500
        },
        "quotas": {
          "monthly_queries": 30,
          "tokens_per_month": 30000,
          "storage_gb": 10,
          "max_users": 5,
          "max_data_sources": 30
        },
        "security": {
          "allowed_email_domains": ["*"],
          "mfa_required": false,
          "ip_allowlist": []
        },
        "compliance": {
          "timezone": "America/Mexico_City",
          "locale": "es-MX",
          "data_region": "mexico-central-1"
        },
        "control": {
          "is_active": true,
          "status": "enabled",
          "tags": ["telecom", "priority"],
          "notes": "Cliente con integración a datos de prueba"
        },
        "audit": {
          "created_at": "2025-10-09T16:05:00Z",
          "created_by": "USERvar(--wb-x-text-27638d76, #27638d76)-598f-4216-8e81-9e7bf2cdf010",
          "updated_at": "2025-10-09T16:05:00Z",
          "updated_by": "USERvar(--wb-x-text-27638d76, #27638d76)-598f-4216-8e81-9e7bf2cdf010"
        },
        "soft_delete": {
          "deleted": false,
          "deleted_at": null
        }
      }

   */
  async getProfile() {

    // Get current user ID from auth session (Cognito sub)
    const userId = await getCurrentUserId();

    if (!userId) {
      throw new Error('No pudimos obtener el identificador del usuario.');
    }

    const url = `${USER_ACCOUNT_BASE_URL}/useraccount/${encodeURIComponent(userId)}`;
    const profile = await fetchWithAuth(url, { method: 'GET' });

    if (!profile) {
      return null;
    }

    const looksLikeDynamo = typeof profile === 'object' && profile !== null && Object.values(profile).some((value) => {
      return value && typeof value === 'object' && (
        'S' in value || 'N' in value || 'BOOL' in value || 'M' in value || 'L' in value
      );
    });

    if (looksLikeDynamo) {
      return normalizeProfileFromDynamo(profile);
    }

    return normalizePlainProfile(profile);
  },


  // Create the current user's profile with default onboarding values
  async createProfile({ accountUuid, companyName, employeesCount, acceptedTerms } = {}) {
    const userId = await getCurrentUserId();

    if (!userId) {
      throw new Error('No pudimos obtener el identificador del usuario.');
    }

    const sanitizeAccountUuid = (value) => {
      if (!value) {
        return '';
      }
      if (typeof value === 'string') {
        return value.trim();
      }
      return String(value).trim();
    };

    const sanitizedAccountUuid = sanitizeAccountUuid(accountUuid);
    const resolvedAccountUuid = sanitizedAccountUuid || generateAccountUuid();
    const normalizedUuidSegment = resolvedAccountUuid.replace(/-/g, '').slice(0, 8) || '00000000';
    const trimmedCompanyName = companyName?.trim();
    const resolvedCompanyName = trimmedCompanyName && trimmedCompanyName.length > 0
      ? trimmedCompanyName
      : `Test Corp ${normalizedUuidSegment}`;
    const parsedEmployeesCount = typeof employeesCount === 'number'
      ? employeesCount
      : parseInt(employeesCount, 10);
    const resolvedEmployeesCount = Number.isFinite(parsedEmployeesCount) && parsedEmployeesCount > 0
      ? parsedEmployeesCount
      : 1;

    const payload = {
      user_id: userId,
      account_uuid: resolvedAccountUuid,
      company_name: resolvedCompanyName,
      employees_count: resolvedEmployeesCount,
      "plan": {"tier": "basic", "status": "trial"},
      // El backend (create_user_account) rechaza el alta si esto no es
      // exactamente `true` -- ver OnboardingDialog.js, que ahora exige el
      // checkbox antes de poder enviar el formulario.
      accepted_terms: acceptedTerms === true
    };

    if (!payload.user_id || !payload.company_name || typeof payload.employees_count !== 'number') {
      throw new Error('El payload de creación del perfil no contiene los campos requeridos.');
    }

    if (!payload.accepted_terms) {
      throw new Error('Debes aceptar los términos de uso para crear tu cuenta.');
    }

    const response = await fetchWithAuth(`${RESOURCE_URL}/useraccount`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (response && typeof response === 'object') {
      return { ...payload, ...response };
    }

    return payload;
  },


  /**
   * Update the current user's company information
   * @param {Object} companyOverrides - An object containing company fields to update
   * @returns {Promise<Object>} The updated company information
   * @throws {Error} If unable to get the user ID or if the update fails
   */
  async updateCompany(companyOverrides = {}) {

    const userId = await getCurrentUserId();

    if (!userId) {
      throw new Error('No pudimos obtener el identificador del usuario.');
    }

    const sanitizeString = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      return String(value).trim();
    };

    const toEmployeesCountString = (value) => {
      if (value === null || value === undefined) {
        return '1';
      }

      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed > 0) {
        return String(parsed);
      }

      const sanitized = sanitizeString(value);
      return sanitized || '1';
    };

    const buildAddress = (address = {}) => {
      const baseAddress = {
        country: '',
        neighborhood: '',
        postal_code: '',
        city: '',
        street: ''
      };

      const safeAddress = address && typeof address === 'object' ? address : {};

      return Object.keys(baseAddress).reduce((accumulator, key) => {
        const camelKey = key === 'postal_code' ? 'postalCode' : key;
        accumulator[key] = sanitizeString(
          safeAddress[key] ??
          safeAddress[camelKey] ??
          baseAddress[key]
        );
        return accumulator;
      }, {});
    };

    const resolvedName = sanitizeString(
      companyOverrides.name ??
      companyOverrides.company_name ??
      companyOverrides.companyName ??
      companyOverrides.legal_name ??
      companyOverrides.legalName ??
      ''
    ) || 'Test Corp 27638d76';

    const payload = {
      company: {
        physical_address: buildAddress(
          companyOverrides.physical_address ??
          companyOverrides.physicalAddress ??
          {}
        ),
        taxid: sanitizeString(companyOverrides.taxid ?? companyOverrides.taxId ?? ''),
        fiscal_address: buildAddress(
          companyOverrides.fiscal_address ??
          companyOverrides.fiscalAddress ??
          {}
        ),
        contact_phone: sanitizeString(companyOverrides.contact_phone ?? companyOverrides.contactPhone ?? ''),
        facebook: sanitizeString(companyOverrides.facebook ?? ''),
        name: resolvedName,
        legal_name: sanitizeString(companyOverrides.legal_name ?? companyOverrides.legalName ?? ''),
        linkedin: sanitizeString(companyOverrides.linkedin ?? ''),
        employees_count: toEmployeesCountString(
          companyOverrides.employees_count ??
          companyOverrides.employeesCount ??
          companyOverrides.employees?.count ??
          companyOverrides.employees ??
          '1'
        ),
        contact_email: sanitizeString(companyOverrides.contact_email ?? companyOverrides.contactEmail ?? '')
      }
    };

    const url = USER_ACCOUNT_BASE_URL + '/useraccount/' + encodeURIComponent(userId) + '/company';

    const payloadJson = JSON.stringify(payload)

    console.log('UserProfileService.updateCompany: updating company with payload', payloadJson );
    
    try {
      const response = await fetchWithAuth(url, {
        method: 'PUT',
        body: payloadJson
      });

      return response ?? payload.company;
    } catch (error) {
      console.error('UserProfileService.updateCompany: unable to update company data', error);
      throw error;
    }
  },


  /**
   * Retrieve a user's profile by user ID
   * @param {string} userId - The user ID to fetch
   * @returns {Promise<Object|null>} The user profile object or null if not found
   * @throws {Error} If the fetch fails
   */
  async getProfileByUserId(userId) {
    if (!userId) {
      throw new Error('userId es requerido');
    }

    const url = `${USER_ACCOUNT_BASE_URL}/useraccount/${encodeURIComponent(userId)}`;
    
    try {
      const profile = await fetchWithAuth(url, { method: 'GET' });

      if (!profile) {
        return null;
      }

      const looksLikeDynamo = typeof profile === 'object' && profile !== null && Object.values(profile).some((value) => {
        return value && typeof value === 'object' && (
          'S' in value || 'N' in value || 'BOOL' in value || 'M' in value || 'L' in value
        );
      });

      if (looksLikeDynamo) {
        return normalizeProfileFromDynamo(profile);
      }

      return normalizePlainProfile(profile);
    } catch (error) {
      console.error('UserProfileService.getProfileByUserId: error fetching user profile', error);
      return null;
    }
  }


};

export default UserProfileService;
